import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "./index";
import { tr } from "@/i18n/i18n";
import { useEffect, useRef, useState } from "react";

export const keys = {
  me: ["me"] as const,
  master: ["master"] as const,
  orders: ["orders"] as const,
  order: (code: string) => ["orders", code] as const,
  users: ["users"] as const,
  directory: ["directory"] as const,
  threads: ["chat", "threads"] as const,
  messages: (code: string) => ["chat", "messages", code] as const,
  audit: ["audit"] as const,
  notifications: ["notifications"] as const,
  authConfig: ["auth-config"] as const,
  sync: ["users-sync"] as const,
  feedback: ["feedback"] as const,
  dmThreads: ["dm", "threads"] as const,
  dmMessages: (peer: string) => ["dm", "messages", peer] as const,
  presence: ["presence"] as const,
  channels: ["registration-channels"] as const,
  pending: ["registrations-pending"] as const,
  mirror: ["team-mirror"] as const,
  resources: ["resources"] as const,
  masterHistory: ["master-history"] as const,
};

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => api.auth.me(), staleTime: 30_000 });
export const useMaster = () => useQuery({ queryKey: keys.master, queryFn: () => api.master.get(), staleTime: 60_000 });
export const useOrders = () => useQuery({ queryKey: keys.orders, queryFn: () => api.orders.list(), refetchInterval: 30_000 });
export const useOrder = (code: string) => useQuery({ queryKey: keys.order(code), queryFn: () => api.orders.get(code) });
export const useUsers = () => useQuery({ queryKey: keys.users, queryFn: () => api.users.list() });
export const useDirectory = () => useQuery({ queryKey: keys.directory, queryFn: () => api.users.directory(), staleTime: 60_000 });
export const useThreads = () => useQuery({ queryKey: keys.threads, queryFn: () => api.chat.threads(), refetchInterval: 15_000 });
export const useMessages = (code: string | null) =>
  useQuery({ queryKey: keys.messages(code ?? ""), queryFn: () => api.chat.messages(code!), enabled: !!code, refetchInterval: 10_000 });
export const useAudit = () => useQuery({ queryKey: keys.audit, queryFn: () => api.audit.list() });
export const useNotifications = () => useQuery({ queryKey: keys.notifications, queryFn: () => api.notifications.list(), refetchInterval: 30_000 });
export const useAuthConfig = () => useQuery({ queryKey: keys.authConfig, queryFn: () => api.auth.config(), staleTime: Infinity });
export const usePendingRequests = () => useQuery({ queryKey: keys.pending, queryFn: () => api.registrations.pending(), refetchInterval: 30_000 });
export const useTeamMirror = () => useQuery({ queryKey: keys.mirror, queryFn: () => api.team.mirror(), staleTime: 30_000 });
export const useApprovalChannels = () => useQuery({ queryKey: keys.channels, queryFn: () => api.registrations.channels(), staleTime: 300_000 });
export const useSyncStatus = () => useQuery({ queryKey: keys.sync, queryFn: () => api.users.syncStatus(), refetchInterval: 60_000 });
export const useDmThreads = () => useQuery({ queryKey: keys.dmThreads, queryFn: () => api.dm.threads(), refetchInterval: 10_000 });
export const useDmMessages = (peer: string | null) =>
  useQuery({ queryKey: keys.dmMessages(peer ?? ""), queryFn: () => api.dm.messages(peer!), enabled: !!peer, refetchInterval: 5_000 });
export const usePresence = () => useQuery({ queryKey: keys.presence, queryFn: () => api.presence.list(), refetchInterval: 30_000 });
export const useFeedbackList = () => useQuery({ queryKey: keys.feedback, queryFn: () => api.feedback.list() });

/** Mutation with success toast, error toast and cache invalidation. */
export function useAction<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  opts: { success?: string | ((r: TResult) => string); invalidate?: QueryKey[]; onSuccess?: (r: TResult) => void } = {},
) {
  const qc = useQueryClient();
  // Always run the latest closure (form state captured at click time, not at first render).
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  });
  return useMutation({
    mutationFn: (args: TArgs) => fnRef.current(args),
    onSuccess: (r) => {
      for (const k of opts.invalidate ?? [keys.orders, keys.threads, keys.notifications]) qc.invalidateQueries({ queryKey: k });
      if (opts.success) toast.success(typeof opts.success === "function" ? opts.success(r) : opts.success);
      opts.onSuccess?.(r);
    },
    onError: (e) => toast.error(e instanceof Error ? tr(e.message) : tr("Something went wrong.")),
  });
}

/** Re-render periodically so countdowns stay current. */
export function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}
